/**
 * Add / edit one site contact (Phase 17 / Task 17.3, DEC-146 §4, DEC-324 D-04, D-09).
 *
 * - Create: name, email, phone, role.
 * - Edit: the same fields (only changed ones are sent — the API's PATCH is strict and
 *   refuses an empty body), deactivate / reactivate (never delete), the contact's sites
 *   (AssignmentsEditor), and "revoke ticket links" (17.5).
 * - Unsubscribed contacts show it plainly, with no way to undo it here: D-09 — only the
 *   person can re-subscribe, from their own email link.
 * - A duplicate address comes back from the server as `email: duplicate` and is shown on
 *   the field.
 */

import * as React from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import type { HaulerOperator } from '../sites-operators/hooks';
import {
  LoadEmailRequestError,
  useCreateContact,
  useRevokeTicketLinks,
  useUpdateContact,
  type ContactFields,
  type SiteContact,
} from './api';
import { AssignmentsEditor } from './AssignmentsEditor';
import {
  buttonStyle,
  dangerButtonStyle,
  errorText,
  helpText,
  inputStyle,
  labelStyle,
  primaryButtonStyle,
} from './styles';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function blankToNull(v: string): string | null {
  const t = v.trim();
  return t === '' ? null : t;
}

function messageFor(err: unknown): string {
  if (err instanceof LoadEmailRequestError && err.reasonFor('email') === 'duplicate') {
    return 'Another active contact for this customer already uses that email address.';
  }
  return 'Could not save the contact. Try again.';
}

export function ContactFormModal({
  open,
  onOpenChange,
  customer,
  contact,
  canManage,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly customer: HaulerOperator;
  /** null = add a new contact. */
  readonly contact: SiteContact | null;
  readonly canManage: boolean;
}): React.JSX.Element {
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [role, setRole] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setName(contact?.name ?? '');
    setEmail(contact?.email ?? '');
    setPhone(contact?.phone ?? '');
    setRole(contact?.role ?? '');
    setError(null);
  }, [open, contact]);

  const create = useCreateContact(customer.id);
  const update = useUpdateContact(customer.id);
  const revoke = useRevokeTicketLinks();
  const busy = create.isPending || update.isPending;

  const submit = (): void => {
    setError(null);
    if (name.trim() === '') return setError('Enter a name.');
    if (!EMAIL_RE.test(email.trim())) return setError('Enter a valid email address.');

    if (contact === null) {
      const payload: ContactFields = {
        name: name.trim(),
        email: email.trim(),
        phone: blankToNull(phone),
        role: blankToNull(role),
      };
      create.mutate(
        { payload },
        {
          onSuccess: () => {
            toast.success('Contact added');
            onOpenChange(false);
          },
          onError: (err) => setError(messageFor(err)),
        },
      );
      return;
    }

    const changed: Partial<ContactFields> = {};
    if (name.trim() !== contact.name) Object.assign(changed, { name: name.trim() });
    if (email.trim().toLowerCase() !== contact.email)
      Object.assign(changed, { email: email.trim() });
    if (blankToNull(phone) !== contact.phone) Object.assign(changed, { phone: blankToNull(phone) });
    if (blankToNull(role) !== contact.role) Object.assign(changed, { role: blankToNull(role) });
    if (Object.keys(changed).length === 0) {
      onOpenChange(false);
      return;
    }
    update.mutate(
      { contactId: contact.id, payload: changed },
      {
        onSuccess: () => {
          toast.success('Contact saved');
          onOpenChange(false);
        },
        onError: (err) => setError(messageFor(err)),
      },
    );
  };

  const toggleActive = (): void => {
    if (contact === null) return;
    update.mutate(
      { contactId: contact.id, payload: { isActive: !contact.isActive } },
      {
        onSuccess: () => {
          toast.success(contact.isActive ? 'Contact deactivated' : 'Contact reactivated');
          onOpenChange(false);
        },
        onError: (err) => setError(messageFor(err)),
      },
    );
  };

  const field = (
    id: string,
    label: string,
    value: string,
    set: (v: string) => void,
    type = 'text',
  ): React.JSX.Element => (
    <div>
      <label htmlFor={id} style={labelStyle}>
        {label}
      </label>
      <input
        id={id}
        type={type}
        style={inputStyle}
        value={value}
        disabled={!canManage}
        onChange={(e): void => set(e.target.value)}
      />
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent style={{ maxWidth: '640px', maxHeight: '90vh', overflowY: 'auto' }}>
        <DialogHeader>
          <DialogTitle>{contact === null ? 'Add contact' : contact.name}</DialogTitle>
        </DialogHeader>

        {contact?.unsubscribedAt != null && (
          <p style={{ ...helpText, color: 'var(--color-danger)' }} data-testid="unsubscribed-note">
            This person unsubscribed from Haul Confirmation emails. Only they can turn emails back
            on, from the link in any earlier email.
          </p>
        )}

        {contact?.emailStatus === 'BOUNCING' && (
          <p style={{ ...helpText, color: 'var(--color-danger)' }} data-testid="bouncing-note">
            Emails to this address are bouncing, so none are being sent. Correct the email address
            to start sending again.
          </p>
        )}
        {contact?.emailStatus === 'COMPLAINED' && (
          <p style={{ ...helpText, color: 'var(--color-danger)' }} data-testid="complained-note">
            This person marked a load email as spam, so they are unsubscribed.
          </p>
        )}

        <div style={{ display: 'grid', gap: '12px' }}>
          {field('contact-name', 'Name', name, setName)}
          {field('contact-email', 'Email', email, setEmail, 'email')}
          {field('contact-phone', 'Phone (optional)', phone, setPhone, 'tel')}
          {field('contact-role', 'Role (optional — e.g. Pumper, Foreman)', role, setRole)}
        </div>
        {error !== null && (
          <p role="alert" style={errorText}>
            {error}
          </p>
        )}

        {contact !== null && (
          <div style={{ marginTop: '20px' }}>
            <AssignmentsEditor customer={customer} contact={contact} canManage={canManage} />
          </div>
        )}

        {canManage && contact !== null && (
          <div style={{ marginTop: '20px' }}>
            <button
              type="button"
              style={buttonStyle}
              disabled={revoke.isPending}
              onClick={(): void =>
                revoke.mutate(
                  { contactId: contact.id },
                  {
                    onSuccess: (r) =>
                      toast.success(
                        r.revoked === 1
                          ? '1 ticket link revoked'
                          : `${String(r.revoked)} ticket links revoked`,
                      ),
                    onError: () => toast.error('Could not revoke ticket links'),
                  },
                )
              }
            >
              Revoke ticket links already sent
            </button>
            <p style={helpText}>
              Use this if an email was forwarded to the wrong person. Their “View ticket” links stop
              working; new emails get new links.
            </p>
          </div>
        )}

        <DialogFooter style={{ marginTop: '20px', gap: '12px', flexWrap: 'wrap' }}>
          {canManage && contact !== null && (
            <button
              type="button"
              style={contact.isActive ? dangerButtonStyle : buttonStyle}
              disabled={busy}
              onClick={toggleActive}
            >
              {contact.isActive ? 'Deactivate' : 'Reactivate'}
            </button>
          )}
          <button type="button" style={buttonStyle} onClick={(): void => onOpenChange(false)}>
            {canManage ? 'Cancel' : 'Close'}
          </button>
          {canManage && (
            <button type="button" style={primaryButtonStyle} disabled={busy} onClick={submit}>
              {busy ? 'Saving…' : contact === null ? 'Add contact' : 'Save'}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
